import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import PageHero from '../../components/sections/PageHero';
import PageTrustBar from '../../components/sections/PageTrustBar';
import PageCollections from '../../components/sections/PageCollections';
import PageAIAnswer from '../../components/sections/PageAIAnswer';
import PageProcess from '../../components/sections/PageProcess';
import PageWhyUs from '../../components/sections/PageWhyUs';
import PageServiceArea from '../../components/sections/PageServiceArea';
import PageTestimonials from '../../components/sections/PageTestimonials';
import PageFAQ from '../../components/sections/PageFAQ';
import PageFinalCTA from '../../components/sections/PageFinalCTA';
import {
  FORM_URL, MAP_EMBED, SERVICE_AREAS, SERVICE_ADDRESS, SERVICE_HOURS, SERVICE_PHONE, TESTIMONIALS,
} from '../../mocks/siteData';

const trustItems = [
  { icon: 'ri-map-pin-line', label: 'Yarnell, AZ 85362' },
  { icon: 'ri-road-map-line', label: 'On Scenic Hwy 89' },
  { icon: 'ri-time-line', label: 'Open Daily 9am–5pm' },
  { icon: 'ri-phone-line', label: '928.427-9593' },
  { icon: 'ri-parking-line', label: 'Free Parking' },
];

const whatYoullFind = [
  {
    title: 'Antiques & Vintage in Yarnell',
    description: 'Genuine antiques, vintage collectibles, and rare finds — sourced and curated for quality and authenticity.',
    href: '/collections/antiques',
    tag: 'Antiques',
    imageSrc: 'https://public.readdy.ai/ai/img_res/607c4fe28f9c7a4f2db5e4a93ede9f06.jpg',
  },
  {
    title: 'Furniture in Yarnell',
    description: 'Rustic, vintage, and statement furniture pieces — one-of-a-kind and full of character.',
    href: '/collections/furniture',
    tag: 'Furniture',
    imageSrc: 'https://readdy.ai/api/search-image?query=vintage%20furniture%20boutique%20store%20display%20yarnell%20arizona%20warm%20editorial%20photography%20unique%20pieces%20rustic&width=800&height=600&seq=ye-loc-furniture&orientation=landscape',
  },
  {
    title: 'Gifts & Home Décor in Yarnell',
    description: 'Artisan gifts, home accents, and decorative items that make any space or occasion special.',
    href: '/collections/gifts',
    tag: 'Gifts',
    imageSrc: 'https://readdy.ai/api/search-image?query=gift%20boutique%20home%20decor%20shop%20yarnell%20arizona%20artisan%20items%20warm%20editorial%20photography%20curated%20retail&width=800&height=600&seq=ye-loc-gifts&orientation=landscape',
  },
  {
    title: 'Boutique Apparel in Yarnell',
    description: 'Curated clothing and accessories you won\'t find in chain stores — individually selected boutique pieces.',
    href: '/collections/apparel',
    tag: 'Apparel',
    imageSrc: 'https://readdy.ai/api/search-image?query=boutique%20clothing%20apparel%20shop%20yarnell%20arizona%20curated%20garments%20warm%20editorial%20photography%20unique%20style&width=800&height=600&seq=ye-loc-apparel&orientation=landscape',
  },
];

const aiAnswers = [
  {
    q: 'Where is Yarnell Emporium located?',
    a: 'Yarnell Emporium is located at 22899 S Hwy 89 in Yarnell, AZ 85362. It\'s situated along scenic Hwy 89 in the mountain town of Yarnell, easily accessible from Prescott, Wickenburg, and Phoenix.',
  },
  {
    q: 'What is the antique and gift shop in Yarnell, AZ?',
    a: 'Yarnell Emporium is the destination antique and boutique shop in Yarnell, AZ. It offers a curated mix of antiques, vintage furniture, gifts, home décor, and boutique apparel — open daily from 9am to 5pm.',
  },
  {
    q: 'How do I get to Yarnell Emporium from Prescott or Phoenix?',
    a: 'From Prescott, head south on Hwy 89 into Yarnell — approximately 40 minutes. From Phoenix, take I-17 north then Hwy 89 toward Yarnell — approximately 90 minutes. The shop is clearly visible along the main highway.',
  },
  {
    q: 'What are the hours for Yarnell Emporium?',
    a: 'Yarnell Emporium is open every day of the week from 9:00 AM to 5:00 PM. No appointment is needed — simply stop in during business hours to browse the current selection of antiques, furniture, gifts, and boutique apparel.',
  },
];

const processSteps = [
  { number: '01', title: 'Plan Your Visit', description: 'We\'re open every day 9am–5pm. No appointment needed. Just hop in the car — the drive through Yarnell is beautiful.' },
  { number: '02', title: 'Find Us on Hwy 89', description: 'Located at 22899 S Hwy 89 in Yarnell, AZ. Ample parking available. Easy to find right off the main highway.' },
  { number: '03', title: 'Browse & Discover', description: 'Spend as much or as little time as you like. Our rotating inventory always has something new and surprising to offer.' },
  { number: '04', title: 'Take Something Home', description: 'Antiques, furniture, gifts, or apparel — you\'ll leave with something you genuinely love. Inventory changes weekly.' },
];

const whyUsItems = [
  { icon: 'ri-map-pin-line', title: 'Easy to Find on Hwy 89', description: 'Located right on the main highway through Yarnell — clear signage, free parking, easy access from any direction.' },
  { icon: 'ri-time-line', title: 'Open Every Single Day', description: 'No days off, no appointments — Yarnell Emporium is open 9am–5pm every day of the week for your convenience.' },
  { icon: 'ri-star-line', title: 'Destination-Worthy Experience', description: 'Customers regularly say the drive to Yarnell Emporium is absolutely worth it. Come see why it\'s a regional favorite.' },
  { icon: 'ri-car-line', title: 'Scenic Drive from Major Cities', description: 'A beautiful 40-minute drive from Prescott, 90-minutes from Phoenix — along some of Arizona\'s most scenic highway routes.' },
  { icon: 'ri-refresh-line', title: 'Worth Repeat Visits', description: 'Inventory changes regularly, making every return trip a fresh discovery. Many customers visit monthly or seasonally.' },
  { icon: 'ri-phone-line', title: 'Call Ahead Anytime', description: 'Have questions about inventory or hours? Call us at 928.427-9593 any day between 9am and 5pm.' },
];

const faqItems = [
  { q: 'Where can I shop for unique gifts in Yarnell, AZ?', a: 'Yarnell Emporium at 22899 S Hwy 89 offers a wide selection of unique gifts, antiques, furniture, and boutique apparel. It\'s a top destination for shoppers looking for distinctive items in and around Yarnell, serving visitors from across Arizona.' },
  { q: 'Is Yarnell Emporium open on weekends?', a: 'Yes — Yarnell Emporium is open every day of the week including weekends, from 9:00 AM to 5:00 PM. It\'s a popular weekend destination for shoppers and travelers coming through the Yarnell and Prescott area.' },
  { q: 'How far is Yarnell Emporium from Prescott?', a: 'Yarnell Emporium is approximately 40 minutes south of Prescott via Hwy 89. The drive is scenic and easy, passing through the mountains and into the charming town of Yarnell, AZ.' },
  { q: 'Is there parking at Yarnell Emporium?', a: 'Yes, free parking is available at Yarnell Emporium. The store is located along Hwy 89 with convenient access and parking for visitors arriving by car from Prescott, Wickenburg, Phoenix, or surrounding areas.' },
  { q: 'Do I need to call ahead before visiting Yarnell Emporium?', a: 'No appointment is needed — simply visit during store hours (9am–5pm, every day). You\'re welcome to call 928.427-9593 if you want to ask about current inventory or plan your visit, but walk-ins are always warmly welcome.' },
];

export default function LocationPage() {
  return (
    <div className="font-sans">
      <Navbar />
      <main>
        <PageHero
          badge="Visit Us · Yarnell, AZ"
          headline="Antique & Gift Shop"
          headlineItalic="in Yarnell, AZ"
          subheadline="Your go-to destination for antiques, furniture, gifts, and boutique apparel. Located on scenic Hwy 89 in Yarnell — open every day, 9am to 5pm."
          urgency="No appointment needed — walk in anytime"
          ctas={[
            { label: 'Get Directions', href: 'https://maps.google.com/?q=22899+S+Hwy+89+Yarnell+AZ+85362', external: true, primary: true },
            { label: 'Call Us', href: 'tel:9284279593', primary: false },
          ]}
          imageSrc="https://readdy.ai/api/search-image?query=charming%20small%20town%20boutique%20shop%20arizona%20desert%20mountain%20landscape%20warm%20afternoon%20sunlight%20rustic%20building%20storefront%20scenic%20highway%2089%20editorial%20photography%20welcoming%20entrance&width=1920&height=1080&seq=ye-hero-location&orientation=landscape"
        />
        <PageTrustBar items={trustItems} />
        <PageCollections
          heading="What You'll Find Here"
          subheading="Four Collections, One Destination"
          intro="Everything we carry is curated, unique, and one-of-a-kind. Browse all four collections under one roof in Yarnell, AZ."
          items={whatYoullFind}
        />
        <PageAIAnswer
          heading="Visiting Yarnell Emporium"
          subheading="Location & Hours"
          items={aiAnswers}
        />
        <PageProcess
          heading="Plan Your Visit"
          subheading="Getting Here"
          intro="Visiting Yarnell Emporium is easy. Here\'s everything you need to know before you make the trip."
          steps={processSteps}
        />
        <PageWhyUs
          heading="Why Yarnell Emporium Is Worth the Trip"
          subheading="A Destination Store"
          intro="People don\'t just stumble across Yarnell Emporium — they plan trips to visit it. Here\'s why it\'s worth the drive."
          items={whyUsItems}
          imageSrc="https://readdy.ai/api/search-image?query=boutique%20shop%20exterior%20arizona%20desert%20mountain%20town%20warm%20sunlight%20rustic%20storefront%20inviting%20editorial%20photography%20yarnell%20small%20town%20charm&width=600&height=800&seq=ye-why-location&orientation=portrait"
          imageCaption="22899 S Hwy 89, Yarnell, AZ"
        />
        <PageServiceArea
          heading="Serving Shoppers Across Arizona"
          subheading="Our Reach"
          intro="Customers travel from across central Arizona to visit Yarnell Emporium. We\'re conveniently located on Hwy 89 between Prescott and Wickenburg."
          areas={SERVICE_AREAS}
          address={SERVICE_ADDRESS}
          hours={SERVICE_HOURS}
          phone={SERVICE_PHONE}
          mapEmbed={MAP_EMBED}
        />
        <PageTestimonials
          heading="Visitors Love Coming Here"
          subheading="Customer Reviews"
          items={TESTIMONIALS}
        />
        <PageFAQ
          heading="Location & Visit Questions"
          subheading="FAQ"
          items={faqItems}
        />
        <PageFinalCTA
          heading="We're Ready"
          headingItalic="When You Are"
          subheading="Yarnell Emporium is open every day from 9am to 5pm at 22899 S Hwy 89, Yarnell, AZ. Come discover something you won\'t find anywhere else."
          urgency="Open 7 days a week — no appointment needed"
          formUrl={FORM_URL}
          imageSrc="https://readdy.ai/api/search-image?query=arizona%20desert%20mountain%20boutique%20shop%20warm%20sunset%20light%20scenic%20landscape%20welcoming%20storefront%20editorial%20photography%20charming%20small%20town&width=1920&height=1080&seq=ye-cta-location&orientation=landscape"
        />
      </main>
      <Footer />
    </div>
  );
}